import React from 'react';
import { Drawer, Form, Input, Select, Button, Dropdown, Menu, Avatar, message } from 'antd';
import { workwiseServices } from '../../../WorkW-Api/workWiseApi';
import defaultProfileImage from '../../../../assets/defult-profile-logo.png';
import worldIcon from '../../../../assets/world.svg';
import lockIcon from '../../../../assets/lock.svg';
import './CreateSlideDrawer.css';

// Small reusable async search Select for users
function PeopleSelect({
    value,
    onChange,
    placeholder,
    disabled,
}) {
    const [options, setOptions] = React.useState([]);
    const [fetching, setFetching] = React.useState(false);

    const fetchSuggestions = React.useMemo(
        () =>
            async (search) => {
                if (!search) {
                    setOptions([]);
                    return;
                }
                setFetching(true);
                try {
                    const payload = {
                        notRequired: [],
                        pageNo: 1,
                        pageSize: 20,
                        referenceId: '00000000-0000-0000-0000-000000000000',
                        removeSelf: true,
                        search,
                        types: [1],
                    };
                    const res = await workwiseServices.getAllSuggestion(payload);
                    const data = res?.data?.data || [];
                    setOptions(
                        data.map((member) => ({
                            value: member.id,
                            label: member.name,
                            meta: member,
                        }))
                    );
                } catch (e) {
                    message.error('Failed to load suggestions');
                } finally {
                    setFetching(false);
                }
            },
        []
    );

    // Debounce typing
    const debouncedSearch = React.useMemo(() => {
        let t;
        return (q) => {
            clearTimeout(t);
            t = setTimeout(() => fetchSuggestions(q), 300);
        };
    }, [fetchSuggestions]);

    return (
        <Select
            mode="multiple"
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            showSearch
            labelInValue={false}
            filterOption={false}
            onSearch={debouncedSearch}
            notFoundContent={fetching ? 'Searching…' : 'Type to search users'}
            disabled={disabled}
            optionLabelProp="label"
        >
            {options.map((opt) => (
                <Select.Option key={opt.value} value={opt.value} label={opt.label}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Avatar size="small" src={opt.meta?.image || defaultProfileImage} />
                        <div>
                            <div style={{ fontWeight: 500 }}>{opt.label}</div>
                            {opt.meta?.designation && (
                                <div style={{ fontSize: 12, color: '#6b7280' }}>{opt.meta.designation}</div>
                            )}
                        </div>
                    </div>
                </Select.Option>
            ))}
        </Select>
    );
}

const PRIVACY = {
    PUBLIC: 'public',
    PRIVATE: 'private',
};

export default function CreateFolderDrawer({
    open,
    onClose,
    onCreate,
}) {
    const [form] = Form.useForm();
    const [privacy, setPrivacy] = React.useState(PRIVACY.PUBLIC);
    const [readers, setReaders] = React.useState([]); // array of ids

    React.useEffect(() => {
        if (!open) {
            form.resetFields();
            setReaders([]);
            setPrivacy(PRIVACY.PUBLIC);
        }
    }, [open, form]);

    const menu = (
        <Menu
            onClick={(e) => {
                if (e.key === PRIVACY.PUBLIC) setPrivacy(PRIVACY.PUBLIC);
                if (e.key === PRIVACY.PRIVATE) setPrivacy(PRIVACY.PRIVATE);
            }}
        >
            <Menu.Item key={PRIVACY.PUBLIC}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <img src={worldIcon} alt="Public" style={{ width: 16, height: 16 }} /> Public
                </span>
            </Menu.Item>
            <Menu.Item key={PRIVACY.PRIVATE}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <img src={lockIcon} alt="Private" style={{ width: 16, height: 16 }} /> Private
                </span>
            </Menu.Item>
        </Menu>
    );

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            const payload = {
                name: values.name.trim(),
                description: values.description || '',
                privacyId: privacy === PRIVACY.PUBLIC ? 1 : 2,
                referenceId: '00000000-0000-0000-0000-000000000000',
                referenceType: 1,
                parentId: '00000000-0000-0000-0000-000000000000',
                members: privacy === PRIVACY.PRIVATE ? readers.map(id => ({ memberId: id })) : [],
            };

            await workwiseServices.addDocumentDirectory(payload);
            message.success('Folder created successfully');
            onCreate?.();
            onClose();
        } catch (error) {
            const errorMessage = error?.response?.data?.message || 'Failed to create folder';
            message.error(errorMessage);
        }
    };

    const CreateButtonBar = (
        <div className="cpd-bar">
            <Dropdown overlay={menu} placement="topLeft" trigger={["click"]}>
                <div className="cpd-privacy">
                    <Button
                        type="text"
                        shape="circle"
                    >
                        {privacy === PRIVACY.PUBLIC ? <img src={worldIcon} alt="Public" style={{ width: 16, height: 16 }} /> : <img src={lockIcon} alt="Private" style={{ width: 16, height: 16 }} />}
                    </Button>
                </div>
            </Dropdown>
            <Button
                type="primary"
                size="large"
                block
                className="cpd-create-btn"
                onClick={handleSubmit}
            >
                Create
            </Button>
        </div>
    );

    return (
        <Drawer
            className="cpd-drawer"
            title={<span style={{ fontWeight: 600, color: '#526bb1' }}>Create Folder</span>}
            visible={open}
            onClose={onClose}
            width={640}
            destroyOnClose={false}
            bodyStyle={{
                background: '#f5f6f7',
                padding: 16
            }}
        >
            <div className="cpd" style={{
                background: '#fff',
                borderRadius: 8,
                padding: '20px'
            }}>
                <div>
                    <Form form={form} layout="vertical" requiredMark={true}>
                        <Form.Item
                            label={<span className="cpd-label">Name</span>}
                            name="name"
                            rules={[{ required: true, message: 'Enter Name' }]}
                        >
                            <Input placeholder="Enter Name" />
                        </Form.Item>

                        {privacy === PRIVACY.PRIVATE && (
                            <Form.Item
                                label={<span className="cpd-label">Access To</span>}
                            >
                                <PeopleSelect
                                    value={readers}
                                    onChange={setReaders}
                                    placeholder="Select Member"
                                />
                            </Form.Item>
                        )}

                        <Form.Item
                            label={<span className="cpd-label">Description</span>}
                            name="description"
                        >
                            <Input.TextArea placeholder="Enter Description" showCount maxLength={500} rows={3} />
                        </Form.Item>
                    </Form>
                </div>

                <div style={{
                    paddingTop: 8
                }}>
                    {CreateButtonBar}
                </div>
            </div>
        </Drawer>
    );
}